/**
 * Industry Detector Service
 * Multi-layer industry detection for maximum reliability
 * Combines: Domain patterns + Content keywords + AI analysis
 */

export interface IndustryDetectionResult {
  industry: string;                 // 'automotive', 'ecommerce', etc.
  confidence: number;               // 0-100
  method: 'domain' | 'content' | 'ai' | 'consensus';
  evidence: string[];               // Why this industry was chosen
  allDetections: IndustryMatch[];   // All detection results for debugging
}

interface IndustryMatch {
  industry: string;
  confidence: number;
  method: string;
  evidence: string[];
  weight: number;
}

export const industryDetector = {
  /**
   * Detect industry using multi-layer approach
   */
  async detectIndustry(
    url: string,
    scrapedContent: string,
    metadata: any,
    aiAnalysis?: any
  ): Promise<IndustryDetectionResult> {
    
    const detections: IndustryMatch[] = [];
    
    // Layer 1: Domain Pattern Analysis (40% weight - most reliable)
    const domainMatch = this.detectFromDomain(url);
    if (domainMatch) {
      detections.push({ ...domainMatch, weight: 40 });
    }
    
    // Layer 2: Content Keyword Analysis (30% weight)
    const contentMatch = this.detectFromContent(scrapedContent, metadata);
    if (contentMatch) {
      detections.push({ ...contentMatch, weight: 30 });
    }
    
    // Layer 3: AI Analysis (30% weight - if provided)
    if (aiAnalysis && aiAnalysis.industry) {
      detections.push({
        industry: aiAnalysis.industry.toLowerCase().replace(/ /g, '_'),
        confidence: aiAnalysis.industryConfidence || 80,
        method: 'ai',
        evidence: [aiAnalysis.industryEvidence || 'AI analysis'],
        weight: 30
      });
    }
    
    // Calculate consensus
    return this.calculateConsensus(detections);
  },

  /**
   * Layer 1: Domain Pattern Detection
   * Highest confidence - domain names are very indicative
   */
  detectFromDomain(url: string): IndustryMatch | null {
    const domain = new URL(url).hostname.toLowerCase();
    
    const patterns = [
      // Automotive (very specific)
      { 
        pattern: /autohaus|kfz|fahrzeug|auto-|car-dealer|mahag|autowelt|autocenter|pkw|gebrauchtwagen/i,
        industry: 'automotive',
        confidence: 95,
        evidence: 'Domain contains automotive keywords'
      },
      
      // E-Commerce
      {
        pattern: /shop|store|online-shop|kaufen|verkaufen|marketplace/i,
        industry: 'ecommerce',
        confidence: 85,
        evidence: 'Domain contains e-commerce keywords'
      },
      
      // Real Estate
      {
        pattern: /immobilien|real-estate|wohnung|haus|immo|wohnungen|property/i,
        industry: 'real_estate',
        confidence: 90,
        evidence: 'Domain contains real estate keywords'
      },
      
      // Finance
      {
        pattern: /bank|finanz|kredit|versicherung|invest|finance|insurance/i,
        industry: 'finance',
        confidence: 90,
        evidence: 'Domain contains financial keywords'
      },
      
      // SaaS/Software
      {
        pattern: /software|saas|app|tool|platform|cloud|tech/i,
        industry: 'professional_services',
        confidence: 75,
        evidence: 'Domain contains software/SaaS keywords'
      },
      
      // Healthcare
      {
        pattern: /arzt|doktor|praxis|klinik|health|medical|doctor|clinic/i,
        industry: 'healthcare',
        confidence: 90,
        evidence: 'Domain contains healthcare keywords'
      }
    ];
    
    for (const { pattern, industry, confidence, evidence } of patterns) {
      if (pattern.test(domain)) {
        return {
          industry,
          confidence,
          method: 'domain',
          evidence: [`${evidence}: ${domain}`],
          weight: 40
        };
      }
    }
    
    return null;
  },

  /**
   * Layer 2: Content Keyword Analysis
   * Scans actual page content for industry indicators
   */
  detectFromContent(scrapedContent: string, metadata: any): IndustryMatch | null {
    const content = scrapedContent.toLowerCase();
    const title = (metadata?.title || '').toLowerCase();
    const description = (metadata?.description || '').toLowerCase();
    const combined = `${title} ${description} ${content.substring(0, 5000)}`;
    
    const industries = [
      {
        industry: 'automotive',
        keywords: [
          'fahrzeug', 'auto', 'kfz', 'gebrauchtwagen', 'neuwagen', 'probefahrt',
          'autohaus', 'händler', 'volkswagen', 'audi', 'bmw', 'mercedes',
          'car dealer', 'test drive', 'vehicle', 'showroom'
        ],
        minMatches: 3
      },
      {
        industry: 'ecommerce',
        keywords: [
          'warenkorb', 'checkout', 'in den warenkorb', 'bestellen', 'kaufen',
          'produkt', 'preis', 'versand', 'add to cart', 'shop', 'store'
        ],
        minMatches: 3
      },
      {
        industry: 'real_estate',
        keywords: [
          'immobilien', 'wohnung', 'haus', 'mieten', 'kaufen', 'quadratmeter',
          'property', 'apartment', 'real estate', 'square meters'
        ],
        minMatches: 3
      },
      {
        industry: 'finance',
        keywords: [
          'finanzierung', 'kredit', 'zinsen', 'versicherung', 'anlage',
          'loan', 'interest', 'insurance', 'investment', 'banking'
        ],
        minMatches: 3
      },
      {
        industry: 'professional_services',
        keywords: [
          'beratung', 'consulting', 'software', 'lösung', 'dienstleistung',
          'demo', 'free trial', 'kostenlos testen', 'saas'
        ],
        minMatches: 2
      }
    ];
    
    const matches: Array<{ industry: string; matchCount: number; matchedKeywords: string[] }> = [];
    
    for (const { industry, keywords, minMatches } of industries) {
      const matchedKeywords: string[] = [];
      let matchCount = 0;
      
      for (const keyword of keywords) {
        if (combined.includes(keyword)) {
          matchCount++;
          matchedKeywords.push(keyword);
        }
      }
      
      if (matchCount >= minMatches) {
        matches.push({ industry, matchCount, matchedKeywords });
      }
    }
    
    // Return industry with most keyword matches
    if (matches.length > 0) {
      matches.sort((a, b) => b.matchCount - a.matchCount);
      const best = matches[0];
      
      // Confidence based on match count
      const confidence = Math.min(50 + (best.matchCount * 10), 95);
      
      return {
        industry: best.industry,
        confidence,
        method: 'content',
        evidence: [`Found ${best.matchCount} industry keywords: ${best.matchedKeywords.slice(0, 5).join(', ')}`],
        weight: 30
      };
    }
    
    return null;
  },

  /**
   * Calculate consensus from multiple detection methods
   * Uses weighted voting to determine best industry match
   */
  calculateConsensus(detections: IndustryMatch[]): IndustryDetectionResult {
    if (detections.length === 0) {
      // Default fallback
      return {
        industry: 'professional_services',
        confidence: 50,
        method: 'consensus',
        evidence: ['No clear industry signals, using default'],
        allDetections: []
      };
    }
    
    // Group by industry
    const industryScores: { [industry: string]: { score: number; methods: string[]; evidence: string[] } } = {};
    
    detections.forEach(detection => {
      if (!industryScores[detection.industry]) {
        industryScores[detection.industry] = {
          score: 0,
          methods: [],
          evidence: []
        };
      }
      
      // Weighted score: confidence * weight
      const weightedScore = (detection.confidence / 100) * detection.weight;
      industryScores[detection.industry].score += weightedScore;
      industryScores[detection.industry].methods.push(detection.method);
      industryScores[detection.industry].evidence.push(...detection.evidence);
    });
    
    // Find industry with highest score
    const sortedIndustries = Object.entries(industryScores)
      .sort((a, b) => b[1].score - a[1].score);
    
    const [bestIndustry, bestData] = sortedIndustries[0];
    
    // Calculate final confidence (normalize score to 0-100)
    const maxPossibleScore = detections.reduce((sum, d) => sum + d.weight, 0);
    const finalConfidence = Math.round((bestData.score / maxPossibleScore) * 100);
    
    return {
      industry: bestIndustry,
      confidence: finalConfidence,
      method: bestData.methods.length > 1 ? 'consensus' : bestData.methods[0] as any,
      evidence: bestData.evidence,
      allDetections: detections
    };
  },

  /**
   * Get display name for industry
   */
  getIndustryDisplayName(industry: string): string {
    const names: { [key: string]: string } = {
      'automotive': 'Automotive / Car Dealer',
      'ecommerce': 'E-Commerce',
      'real_estate': 'Real Estate',
      'finance': 'Finance',
      'professional_services': 'Professional Services',
      'healthcare': 'Healthcare',
      'saas': 'SaaS / Software'
    };
    
    return names[industry] || industry;
  }
};

